import { useEffect, useState } from 'react';
import uuid from 'uuid';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { chatgenieAPI } from 'src/config/api';
import useToast from 'src/hooks/useToast';
import { extractServerErrorMessage } from 'src/utils';
import queryKeys from 'src/constants/queryKeys';
import {
  getDefaultArgumentData,
  getDefaultVariationData,
  normalizeIntentData,
  normalizeTemplateIntentData
} from './Variation/utils';
import {
  SCOPE_ALL_CLIENTS,
  SCOPE_COMPANY_TYPES,
  SCOPE_CONTACT_TYPES,
  SCOPE_PARENT_COMPANY,
  SCOPE_SPECIFIC_CLIENTS,
  SCOPE_TOP_LEVEL
} from './constants';

export const useIntent = () => {
  const { intentId } = useParams();
  const [data, setData] = useState({
    name: '',
    description: '',
    variations: [getDefaultVariationData()],
    time_saved: 30,
    is_active: true
  });
  const [selectedVariationIndex, setSelectedVariationIndex] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const variation = data.variations[selectedVariationIndex];
  const history = useHistory();
  const location = useLocation();
  const { template } = location.state || {};
  const { toast } = useToast();

  const {
    data: res,
    isLoading,
    isFetching
  } = useQuery([queryKeys.GET_AGENTS, intentId], () => chatgenieAPI.getAgent(intentId), {
    enabled: !!intentId,
    refetchOnMount: true
  });
  const { mutate, isLoading: isUpserting } = useMutation(
    data => {
      if (intentId) {
        return chatgenieAPI.updateAgent({ ...data, id: intentId });
      }
      return chatgenieAPI.createAgent(data);
    },
    {
      onSuccess: () => {
        history.push({
          pathname: '/dashboard/magic/agent',
          state: { isSaved: true }
        });
      },
      onError: error => {
        const errorMessage = extractServerErrorMessage(error);
        toast.error(`Failed to ${intentId ? 'update' : 'create'} intent: ${errorMessage}`);
      }
    }
  );

  const updateIntent = newState => {
    setIsDirty(true);
    setData(newState);
  };

  const validateIntersection = variations => {
    const checkIntersection = items => {
      const seenIds = new Set();
      return items.some(item => {
        if (seenIds.has(item)) {
          return true;
        }
        seenIds.add(item);
        return false;
      });
    };

    const fields = ['clients', 'company_types', 'contact_types', 'parent_companies', 'top_levels'];

    const intersectedField = fields.find(field =>
      checkIntersection(variations.flatMap(variation => variation[field]))
    );

    if (intersectedField) {
      return [false, intersectedField.replace('_', ' ')];
    }

    return [true, ''];
  };

  const handleSubmit = e => {
    e.preventDefault();

    const normalizedData = {
      ...data,
      template_id: template?.id,
      variations: data.variations.map(item => {
        const replies = item.replies.filter(reply => {
          if (!reply.id && !reply.description) {
            return false;
          }
          return true;
        });

        let variationArguments = item.arguments.map(arg => ({
          ...arg,
          id: typeof arg.id === 'string' ? undefined : arg.id,
          type: arg.type?.value || null,
          type_data: arg.type_data
            ? JSON.stringify(arg.type_data.filter(el => el.value).map(choice => choice.value))
            : null
        }));

        const hasEmptyArguments =
          variationArguments.length === 1 &&
          !variationArguments[0].name &&
          !variationArguments[0].description;

        if (hasEmptyArguments) {
          variationArguments = [];
        }

        const automationHeaders = item.headers.reduce((acc, header) => {
          if (header.key.trim() || header.value.trim()) {
            acc[header.key] = header.value;
          }
          return acc;
        }, {});

        return {
          ...item,
          id: typeof item.id === 'string' ? undefined : item.id,
          approval: {
            ...item?.approval,
            contact_types: item?.approval?.contact_types?.map(type => type.id)
          },
          clients:
            item.visibility_scope === SCOPE_SPECIFIC_CLIENTS
              ? item.clients.map(client => client.id)
              : [],
          company_types:
            item.visibility_scope === SCOPE_COMPANY_TYPES
              ? item.company_types.map(type => type.id)
              : [],
          contact_types:
            item.visibility_scope === SCOPE_CONTACT_TYPES
              ? item.contact_types.map(type => type.id)
              : [],
          parent_companies:
            item.visibility_scope === SCOPE_PARENT_COMPANY
              ? item.parent_companies.map(type => type.id)
              : [],
          top_levels:
            item.visibility_scope === SCOPE_TOP_LEVEL ? item.top_levels.map(level => level.id) : [],
          arguments: variationArguments,
          replies,
          method: item.method?.value,
          headers: automationHeaders
        };
      })
    };

    // Check for intersected ids in clients within variations
    const [isValid, fieldName] = validateIntersection(normalizedData.variations);
    if (!isValid) {
      toast.warn(`Variations cannot have the same ${fieldName}`);
      return;
    }

    mutate(normalizedData);
  };

  const updateSelectedVariation = callback => {
    updateIntent(prev => ({
      ...prev,
      variations: prev.variations.map((item, index) => {
        if (index === selectedVariationIndex) {
          return callback(item);
        }
        return item;
      })
    }));
  };

  const handleRemoveArgument = id => {
    const canArgumentsBeEmpty = variation.replies.some(el => el.description);
    if (variation.arguments.length === 1 && !canArgumentsBeEmpty) {
      toast.warn('There must be at least one form field or a reply set up.');
      return;
    }

    updateSelectedVariation(prev => ({
      ...prev,
      arguments: prev.arguments
        .filter(arg => arg.id !== id)
        .map((arg, index) => ({
          ...arg,
          order: index + 1
        }))
    }));
  };

  const handleChangeFormField = newArgumentdata => {
    updateSelectedVariation(prev => ({
      ...prev,
      arguments: prev.arguments.map((item, index) =>
        item.id === newArgumentdata.id
          ? {
              ...newArgumentdata,
              order: index + 1
            }
          : {
              ...item,
              order: index + 1
            }
      )
    }));
  };

  const handleSelectVariation = index => setSelectedVariationIndex(index);

  const handleAddFormField = () => {
    updateSelectedVariation(prev => ({
      ...prev,
      arguments: [...prev.arguments, getDefaultArgumentData(prev.arguments.length)]
    }));
  };

  const handleDescriptionChange = value => {
    updateIntent(prev => ({
      ...prev,
      description: value
    }));
  };

  const handleTitleChange = value => {
    updateIntent(prev => ({
      ...prev,
      name: value
    }));
  };

  const handleIsActive = isActive => {
    updateIntent(prev => ({
      ...prev,
      is_active: isActive
    }));
  };

  const handleChangeAutomation = automation => {
    updateSelectedVariation(prev => ({
      ...prev,
      url: automation.url ?? prev.url,
      method: automation.method ?? prev.method,
      headers: automation.headers ?? prev.headers
    }));
  };

  const handleChangeApproval = value => {
    updateSelectedVariation(prev => ({
      ...prev,
      approval: {
        ...prev.approval,
        is_active: value
      }
    }));
  };

  const handleApprovalsCompanyTypes = value => {
    updateSelectedVariation(prev => ({
      ...prev,
      approval: {
        ...prev.approval,
        contact_types: value
      }
    }));
  };

  const handleVariationNameChange = value => {
    updateSelectedVariation(prev => ({
      ...prev,
      name: value,
      approval: {
        ...prev.approval,
        name: `Approval for: ${value}`
      }
    }));
  };

  const handleClientsChange = clients => {
    updateSelectedVariation(prev => ({
      ...prev,
      clients
    }));
  };

  const handleCompanyTypesChange = companyTypes => {
    updateSelectedVariation(prev => ({
      ...prev,
      company_types: companyTypes
    }));
  };

  const handleParentCompaniesChange = parentCompanies => {
    updateSelectedVariation(prev => ({
      ...prev,
      parent_companies: parentCompanies
    }));
  };

  const handleContactTypesChange = contactTypes => {
    updateSelectedVariation(prev => ({
      ...prev,
      contact_types: contactTypes
    }));
  };

  const handleTopLevelsChange = topLevels => {
    updateSelectedVariation(prev => ({
      ...prev,
      top_levels: topLevels
    }));
  };

  const handleVisibilityScopeChange = value => {
    updateSelectedVariation(prev => ({
      ...prev,
      visibility_scope: value
    }));
  };

  const handleAddNewVariation = () => {
    const defaultVariationData = getDefaultVariationData();
    updateIntent({
      ...data,
      variations: [
        ...data.variations,
        {
          ...defaultVariationData,
          intent_id: data.id,
          id: uuid.v4(),
          is_default: false,
          name: `Variation ${data.variations.length}`,
          visibility_scope: SCOPE_SPECIFIC_CLIENTS,
          approval: {
            ...defaultVariationData.approval,
            name: `Approval for: Variation ${data.variations.length}`
          }
        }
      ]
    });
    setSelectedVariationIndex(data.variations.length);
  };

  const handleDeleteVariation = variationItem => {
    if (variationItem.is_default) {
      toast.error(`You could not delete default variation, try to delete whole intent`);
      return;
    }

    if (variationItem.id === variation.id) {
      setSelectedVariationIndex(data.variations.findIndex(item => item.is_default));
    }

    updateIntent({
      ...data,
      variations: data.variations.filter(item => item.id !== variationItem.id)
    });
  };

  const handleDuplicateVariation = variationItem => {
    const formFields = variationItem.arguments.map(arg => ({
      ...arg,
      id: uuid.v4()
    }));

    const duplicatedVariation = {
      ...variationItem,
      visibility_scope:
        variationItem.visibility_scope === SCOPE_ALL_CLIENTS
          ? SCOPE_SPECIFIC_CLIENTS
          : variationItem.visibility_scope,
      arguments: formFields,
      id: uuid.v4(),
      name: `${variationItem.name} (Copy)`,
      is_default: false
    };

    updateIntent({
      ...data,
      variations: [...data.variations, duplicatedVariation]
    });
    setSelectedVariationIndex(data.variations.length);
  };

  const handleChangeArgumentOrder = (startIndex, endIndex) => {
    updateSelectedVariation(prev => {
      const result = Array.from(prev.arguments);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);

      return {
        ...prev,
        arguments: result.map((arg, index) => ({
          ...arg,
          order: index + 1
        }))
      };
    });
  };

  const handleReplyContentChange = (type, content) => {
    updateSelectedVariation(prev => ({
      ...prev,
      replies: prev.replies.map(reply =>
        reply.type === type
          ? {
              ...reply,
              ...content
            }
          : reply
      )
    }));
  };

  const handleTimeSavedChange = timeSaved => {
    updateIntent({
      ...data,
      time_saved: timeSaved
    });
  };

  const handleVariationToggle = isActive => {
    updateSelectedVariation(prev => ({
      ...prev,
      is_active: isActive
    }));
  };

  useEffect(() => {
    if (res) {
      setData(old => ({
        ...old,
        ...normalizeIntentData(res)
      }));
    }
  }, [res]);

  useEffect(() => {
    if (template) {
      setData(old => ({
        ...old,
        ...normalizeTemplateIntentData(template)
      }));
      setIsDirty(true);
    }
  }, [template]);

  return {
    data,
    isLoading: isLoading || isFetching,
    variation,
    isUpserting,
    isDirty,
    handleDuplicateVariation,
    handleDeleteVariation,
    handleAddNewVariation,
    handleVariationNameChange,
    handleClientsChange,
    handleVisibilityScopeChange,
    handleChangeAutomation,
    handleChangeApproval,
    handleApprovalsCompanyTypes,
    handleAddFormField,
    handleChangeFormField,
    handleDescriptionChange,
    handleRemoveArgument,
    handleSelectVariation,
    handleSubmit,
    handleTitleChange,
    handleIsActive,
    handleReplyContentChange,
    handleChangeArgumentOrder,
    handleCompanyTypesChange,
    handleContactTypesChange,
    handleParentCompaniesChange,
    handleTopLevelsChange,
    handleTimeSavedChange,
    handleVariationToggle
  };
};
